import React, { useState, useEffect, useRef } from 'react';
import { getSourceDisplayName, getSourceUrl } from '../utils/sourceRegistry';
import { getCoaching } from '../utils/api';
import DocViewerModal from './DocViewerModal';

/**
 * CoachingPanel - A chat-like interface for real-time mentor guidance.
 * Displays messages, safety warnings, and reasoning/sources.
 */
// Prevent double initial send (e.g. React Strict Mode double-mount)
const initialSendKeys = new Set();

function CoachingPanel({ ticketId, stepAction, initialQuestion, isOnline = true }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });
  const scrollRef = useRef(null);
  const initialKeyRef = useRef(null);

  // Auto-scroll to bottom on new messages or loading state change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Trigger the initial mentor response once per panel open (ticketId + step)
  useEffect(() => {
    if (!ticketId || !initialQuestion || messages.length > 0) return;
    const key = `${ticketId}|${stepAction ?? ''}|${initialQuestion}`;
    if (initialSendKeys.has(key)) return;
    initialSendKeys.add(key);
    initialKeyRef.current = key;
    if (isOnline) {
      handleSendMessage(initialQuestion, true); // silent = true (don't show user question in bubble)
    } else {
      setMessages([{
        role: 'model',
        content: 'Coaching is limited in offline mode. Follow the step instructions carefully. If unsure, use the Emergency Escalate button.',
        safety: 'Always ensure equipment is powered down before inspection.',
      }]);
    }
    return () => {
      if (initialKeyRef.current) {
        initialSendKeys.delete(initialKeyRef.current);
        initialKeyRef.current = null;
      }
    };
  }, [ticketId, stepAction, initialQuestion, isOnline]);

  const handleSendMessage = async (text = null, silent = false) => {
    const question = text || inputValue;
    if (!question.trim()) return;
    if (!ticketId && isOnline) {
      setMessages(prev => [...prev, { role: 'model', content: "No ticket loaded. Open a ticket first to use coaching." }]);
      return;
    }
    if (!isOnline) {
      setMessages(prev => [...prev, { role: 'model', content: "Coaching is unavailable offline. Follow the step instructions and escalate if needed." }]);
      return;
    }

    if (!silent) {
      const userMsg = { role: 'user', content: question };
      setMessages(prev => [...prev, userMsg]);
    }
    
    if (!text) setInputValue('');
    setLoading(true);

    try {
      // Use messages for history context
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      const data = await getCoaching(
        ticketId,
        null, // current_step_number is optional
        stepAction ?? undefined,
        question,
        history
      );

      // Add model response (skip if duplicate, e.g. from double initial send)
      const newMsg = { 
        role: 'model', 
        content: data.response,
        safety: data.safety_reminder,
        reasoning: data.reasoning,
        sources: data.sources
      };
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'model' && last?.content === data.response) return prev;
        return [...prev, newMsg];
      });
    } catch (err) {
      console.error("Coaching Error:", err);
      setMessages(prev => [...prev, { 
        role: 'model', 
        content: "I'm having trouble connecting to the mentor service. Please check your connection or try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    const rawText = text || "";
    try {
      if (window.marked) {
        return { __html: window.marked.parse(rawText) };
      }
    } catch (e) {
      console.error("Markdown Error", e);
    }
    return { __html: rawText };
  };

  return (
    <div className="coaching-panel">
      <div className="coaching-panel__header">
        <span>💡</span> FIELD MENTOR
      </div>
      
      <div className="coaching-messages" ref={scrollRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`message-bubble ${msg.role}`}>
            <div 
              className="message-content" 
              dangerouslySetInnerHTML={msg.role === 'model' ? renderMarkdown(msg.content) : {__html: msg.content}} 
            />
            {msg.safety && (
              <div className="message-safety">⚠ SAFETY: {msg.safety}</div>
            )}
            {msg.reasoning && (
              <details className="message-reasoning message-reasoning--chain" open={false}>
                <summary>Chain of thought & sources</summary>
                <div className="reasoning-detail reasoning-detail--steps" dangerouslySetInnerHTML={renderMarkdown(msg.reasoning)} />
                {msg.sources && msg.sources.length > 0 && (
                  <div className="sources-list">
                    <strong>Knowledge base used (RAG)</strong>
                    <p className="sources-list__hint">Specific documents used for this answer. Click to open the source:</p>
                    <ul className="sources-list__items rag-sources-list" aria-label="RAG source documents">
                      {msg.sources.map((src, idx) => {
                        const filename = typeof src === 'string' ? src.trim() : String(src);
                        const displayName = getSourceDisplayName(filename);
                        const href = getSourceUrl(filename);
                        return (
                          <li key={idx} className="sources-list__item rag-source-item">
                            <span className="rag-source-item__name">{displayName}</span>
                            <button type="button" className="sources-list__link rag-source-item__link" onClick={() => setDocViewer({ open: true, url: href, title: displayName })}>View document →</button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </details>
            )}
          </div>
        ))}
        {loading && (
          <div className="message-bubble model loading">
            <div className="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
      </div>

      <DocViewerModal isOpen={docViewer.open} url={docViewer.url} title={docViewer.title} onClose={() => setDocViewer({ open: false, url: '', title: '' })} />

      <div className="coaching-input">
        <input 
          type="text" 
          placeholder="Ask your mentor..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          disabled={loading}
        />
        <button onClick={() => handleSendMessage()} disabled={loading || !inputValue.trim()}>
          Send
        </button>
      </div>
    </div>
  );
}

export default CoachingPanel;
